<template>
  <!-- The desktop a support session runs in (utils/support_session): a monitor
       drawn over the admin, with the organization's app in it — signed in as
       the organization's admin — and the controls to end it. Everything stays
       in this tab: the admin pages wait underneath, untouched, until the
       session ends and the desktop goes. -->
  <div
    class="ssd"
    :class="[`ssd--${phase}`, { 'ssd--max': maximized }]"
    role="dialog"
    aria-modal="true"
    :aria-label="`Support session in ${appInfo.name} with ${session.organizationName}`"
  >
    <div class="ssd__desk">
      <div class="ssd__monitor">
        <header class="ssd__bar">
          <div class="ssd__brand">
            <span class="ssd__mark" aria-hidden="true"><img :src="mark" alt="" /></span>
            <div class="ssd__titles">
              <span class="ssd__title">
                <span class="ssd__app">{{ appInfo.name }}</span>
                <span class="ssd__pill"><i class="pi pi-headphones" aria-hidden="true"></i>Support session</span>
              </span>
              <span class="ssd__subtitle" :title="`Signed in to ${session.organizationName} as Admin, as ${actorName}`">
                {{ session.organizationName }} · as Admin · {{ actorName }}
              </span>
            </div>
          </div>

          <!-- Where the app is: its address, and the page it is on. -->
          <div class="ssd__address" :title="address">
            <i class="pi pi-lock" aria-hidden="true"></i>
            <span class="ssd__host">{{ appInfo.host }}</span><span class="ssd__path">{{ path }}</span>
          </div>

          <div class="ssd__controls">
            <span class="ssd__timer" :class="{ 'is-low': isRunningLow }" :title="timerTitle">
              <span v-if="phase === 'active'" class="ssd__live" aria-hidden="true"></span>
              <i v-else class="pi pi-clock" aria-hidden="true"></i>
              <span>{{ timerText }}</span>
            </span>
            <button
              type="button"
              class="ssd__btn"
              title="Reload the page"
              aria-label="Reload the page"
              :disabled="phase === 'ended' || ending"
              @click="reload"
            >
              <i class="pi pi-refresh" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              class="ssd__btn"
              :title="maximized ? 'Show the desk' : 'Fill the screen'"
              :aria-label="maximized ? 'Show the desk' : 'Fill the screen'"
              :aria-pressed="maximized ? 'true' : 'false'"
              @click="maximized = !maximized"
            >
              <i class="pi" :class="maximized ? 'pi-window-minimize' : 'pi-window-maximize'" aria-hidden="true"></i>
            </button>
            <button v-if="phase !== 'ended'" type="button" class="ssd__end" :disabled="ending" @click="askEnd">
              <span v-if="ending" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <i v-else class="pi pi-power-off" aria-hidden="true"></i>
              <span>{{ ending ? 'Ending…' : 'End session' }}</span>
            </button>
            <button v-else type="button" class="ssd__end ssd__end--close" @click="dismiss">
              <i class="pi pi-times" aria-hidden="true"></i>
              <span>Close</span>
            </button>
          </div>
        </header>

        <div class="ssd__screen">
          <!-- Named, so the app knows it is in a session's frame on any page. -->
          <iframe
            ref="frame"
            :key="frameKey"
            class="ssd__frame"
            :name="SUPPORT_SESSION_FRAME_NAME"
            :src="frameUrl"
            :title="`${appInfo.name} — ${session.organizationName}`"
            allow="clipboard-read; clipboard-write; fullscreen"
            referrerpolicy="strict-origin-when-cross-origin"
            @load="onFrameLoad"
          ></iframe>

          <!-- Over the frame until the app is signed in, and again once the
               session is over. -->
          <div v-if="phase !== 'active'" class="ssd__veil" :class="`ssd__veil--${phase}`">
            <div class="ssd__card" role="status" aria-live="polite">
              <template v-if="phase === 'booting'">
                <span class="ssd__spinner" aria-hidden="true"><img :src="mark" alt="" /></span>
                <h2 class="ssd__card-title">Starting your session in {{ appInfo.name }}…</h2>
                <p class="ssd__card-text">
                  {{ appReady ? `Signing in to ${session.organizationName} as Admin.` : `Opening ${appInfo.host}…` }}
                </p>
              </template>

              <template v-else-if="phase === 'stalled' || phase === 'failed'">
                <span class="ssd__glyph ssd__glyph--warn" aria-hidden="true"><i class="pi pi-exclamation-triangle"></i></span>
                <h2 class="ssd__card-title">{{ problemCopy.title }}</h2>
                <p class="ssd__card-text">{{ problemCopy.text }}</p>
                <div class="ssd__card-actions">
                  <button type="button" class="btn btn-custom-primary ssd__card-btn" @click="retry">
                    <i class="pi pi-refresh" aria-hidden="true"></i>
                    <span>Try again</span>
                  </button>
                  <button type="button" class="btn btn-custom-secondary ssd__card-btn" @click="askEnd">End session</button>
                </div>
              </template>

              <template v-else>
                <span class="ssd__glyph" aria-hidden="true"><i class="pi pi-power-off"></i></span>
                <h2 class="ssd__card-title">{{ endedCopy.title }}</h2>
                <p class="ssd__card-text">{{ endedCopy.text }}</p>
                <div class="ssd__card-actions">
                  <button type="button" class="btn btn-custom-primary ssd__card-btn" @click="dismiss">
                    <i class="pi pi-arrow-left" aria-hidden="true"></i>
                    <span>Back to the admin</span>
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="ssd__stand" aria-hidden="true">
        <span class="ssd__neck"></span>
        <span class="ssd__base"></span>
      </div>
    </div>

    <confirm-modal
      v-if="confirmingEnd"
      :title="`End the session with ${session.organizationName}?`"
      subtitle="The window closes and you're back in the admin."
      icon="pi-power-off"
      tone="danger"
      confirm-label="End session"
      :busy="ending"
      @confirm="confirmEnd"
      @close="confirmingEnd = false"
    />
  </div>
</template>
<script>
import confirmModal from '@/components/Confirm_Modal.vue';
import chabadDigitalMark from '@/assets/images/brands/chabad-digital-icon.png';
import screenkodeMark from '@/assets/images/logo-icon.png';
import {
  DISMISS_SUPPORT_SESSION,
  END_SUPPORT_SESSION,
  NOTE_SUPPORT_SESSION_PATH,
  RELEASE_SUPPORT_SESSION,
} from '@/store/action_types';
import { TOAST_GLOBAL_CONFIG } from '@/utils/const';
import { accountDisplayName } from '@/utils/display';
import {
  SUPPORT_SESSION_BOOT_TIMEOUT_MS,
  SUPPORT_SESSION_CHANNEL,
  SUPPORT_SESSION_FRAME_NAME,
  SUPPORT_SESSION_MESSAGE,
  SUPPORT_SESSION_READY_GRACE_MS,
  durationLabel,
  plainCopy,
  supportSessionApp,
  supportSessionEndedCopy,
  supportSessionFrameUrl,
  supportSessionOrigin,
  supportSessionStallCopy,
} from '@/utils/support_session';

const MARKS = { screenkode: screenkodeMark, chabad_digital: chabadDigitalMark };

// On <body> while the desktop is up: the page underneath must not scroll.
const BODY_CLASS = 'ssd-open';

// The timer turns amber with this much left.
const LOW_TIME_MS = 10 * 60 * 1000;

// Stands in for the session for the moment between it closing and this
// desktop going, so nothing here reads a field off null.
const NO_SESSION = Object.freeze({
  id: '',
  organizationId: '',
  organizationName: '',
  organizationLogo: null,
  app: 'screenkode',
  role: 'admin',
  startedAt: 0,
  expiresAt: 0,
  sessionToken: '',
  account: {},
  lastPath: null,
});

export default {
  name: 'SupportSessionDesktop',
  components: {
    confirmModal,
  },
  data() {
    // A session kept across a reload of the admin opens where it was.
    const kept = this.$store.getters.supportSession;
    const resumeAt = (kept && kept.lastPath) || '/';
    return {
      SUPPORT_SESSION_FRAME_NAME,
      // booting: the frame is loading and signing in; active: in use;
      // stalled: the app never finished signing in (see `stall`); failed:
      // the session couldn't be handed over; ended: over, says why.
      phase: 'booting',
      stall: null,
      failure: '',
      endedReason: null,
      endedMessage: null,
      // The page the app is on, from the app itself.
      path: resumeAt,
      // The page the frame opens at. Changed only to load it afresh.
      framePath: resumeAt,
      maximized: false,
      // Bumped to load the frame afresh.
      frameKey: 0,
      // Kept from mount: the app's origin, to talk to the frame with, even
      // once the session has left the store.
      frameOrigin: '',
      // What the frame has done so far, to say why when it gets stuck.
      frameLoaded: false,
      appReady: false,
      confirmingEnd: false,
      ending: false,
      now: Date.now(),
      bootTimer: null,
      readyTimer: null,
      ticker: null,
    };
  },
  computed: {
    session() {
      return this.$store.getters.supportSession || NO_SESSION;
    },
    account() {
      return this.$store.getters.account.data;
    },
    appInfo() {
      return supportSessionApp(this.session);
    },
    mark() {
      return MARKS[this.appInfo.value] || screenkodeMark;
    },
    // Reads the session's id and app only, so the frame never reloads as the
    // app moves between pages.
    frameUrl() {
      return supportSessionFrameUrl(this.session, this.framePath);
    },
    actorName() {
      return accountDisplayName(this.account);
    },
    address() {
      return `${this.appInfo.host}${this.path}`;
    },
    remainingMs() {
      return new Date(this.session.expiresAt).getTime() - this.now;
    },
    isRunningLow() {
      return this.phase !== 'ended' && this.remainingMs < LOW_TIME_MS;
    },
    timerText() {
      if (this.phase === 'ended') return 'Ended';
      if (this.remainingMs <= 0) return 'Expiring…';
      return `Ends in ${durationLabel(this.remainingMs / 1000)}`;
    },
    timerTitle() {
      const started = new Date(this.session.startedAt);
      const ends = new Date(this.session.expiresAt);
      if (Number.isNaN(started.getTime()) || Number.isNaN(ends.getTime())) return '';
      return `Started ${durationLabel((this.now - started.getTime()) / 1000)} ago · ends by itself at ${ends.toLocaleString()}`;
    },
    // What went wrong, while the session couldn't start.
    problemCopy() {
      if (this.phase === 'failed') return { title: "The session couldn't start", text: this.failure };
      return supportSessionStallCopy(this.stall, this.session);
    },
    endedCopy() {
      return supportSessionEndedCopy(this.endedReason, this.endedMessage, this.session);
    },
  },
  methods: {
    // Only the app in the frame is listened to: its origin, its window, and
    // messages on the session's channel.
    onMessage(event) {
      if (!this.frameOrigin || event.origin !== this.frameOrigin) return;
      const frame = this.$refs.frame;
      if (!frame || event.source !== frame.contentWindow) return;
      const data = event.data;
      if (!data || typeof data !== 'object' || data.channel !== SUPPORT_SESSION_CHANNEL) return;

      switch (data.type) {
        // Up and asking for its session: at first, or after it reloaded
        // itself without one. The same session is handed over again.
        case SUPPORT_SESSION_MESSAGE.READY:
          if (this.phase === 'ended' || this.phase === 'failed') return;
          this.appReady = true;
          this.clearReadyTimer();
          this.sendStart();
          break;
        case SUPPORT_SESSION_MESSAGE.STARTED:
          if (this.phase === 'ended' || this.phase === 'failed') return;
          this.clearTimers();
          this.phase = 'active';
          this.stall = null;
          this.notePath(data.path);
          break;
        case SUPPORT_SESSION_MESSAGE.NAVIGATED:
          if (this.phase !== 'ended') this.notePath(data.path);
          break;
        case SUPPORT_SESSION_MESSAGE.ENDED:
          this.onEnded(
            typeof data.reason === 'string' ? data.reason : null,
            typeof data.message === 'string' ? data.message : null
          );
          break;
        case SUPPORT_SESSION_MESSAGE.FAILED:
          if (this.phase !== 'ended') {
            this.fail(
              typeof data.message === 'string' && data.message
                ? data.message
                : `${this.appInfo.name} couldn't use the session it was sent.`
            );
          }
          break;
        default:
          break;
      }
    },
    // To the app in the frame, and only there, as plain data: what comes out
    // of the store is reactive, which postMessage can't send. False when it
    // couldn't be sent.
    sendToFrame(message) {
      const frame = this.$refs.frame;
      if (!frame || !frame.contentWindow || !this.frameOrigin) return false;
      try {
        frame.contentWindow.postMessage(plainCopy({ channel: SUPPORT_SESSION_CHANNEL, ...message }), this.frameOrigin);
        return true;
      } catch (error) {
        console.error('Could not reach the app in the support session.', error);
        return false;
      }
    },
    // The token and the account the app signs in as, to the app alone.
    sendStart() {
      const session = this.session;
      if (!session.sessionToken) return;
      const sent = this.sendToFrame({
        type: SUPPORT_SESSION_MESSAGE.START,
        sessionToken: session.sessionToken,
        account: session.account,
        session: {
          id: session.id,
          organizationId: session.organizationId,
          organizationName: session.organizationName,
          app: session.app,
          role: session.role,
          startedAt: session.startedAt,
          expiresAt: session.expiresAt,
        },
      });
      if (!sent) this.fail(`The session couldn't be handed to ${this.appInfo.name}. Try again.`);
    },
    notePath(path) {
      if (typeof path !== 'string' || !path.startsWith('/')) return;
      this.path = path;
      this.$store.dispatch(NOTE_SUPPORT_SESSION_PATH, path);
    },
    onFrameLoad() {
      this.frameLoaded = true;
      // Loaded: the app asks for its session within moments, or it can't.
      if (this.phase === 'booting' && !this.appReady) this.armReadyTimer();
    },
    armBootTimer() {
      this.clearBootTimer();
      this.bootTimer = setTimeout(() => {
        this.bootTimer = null;
        if (this.phase !== 'booting') return;
        this.stallWith(!this.frameLoaded ? 'unreachable' : !this.appReady ? 'outdated' : 'no_signin');
      }, SUPPORT_SESSION_BOOT_TIMEOUT_MS);
    },
    armReadyTimer() {
      this.clearReadyTimer();
      this.readyTimer = setTimeout(() => {
        this.readyTimer = null;
        if (this.phase === 'booting' && !this.appReady) this.stallWith('outdated');
      }, SUPPORT_SESSION_READY_GRACE_MS);
    },
    clearBootTimer() {
      if (this.bootTimer) {
        clearTimeout(this.bootTimer);
        this.bootTimer = null;
      }
    },
    clearReadyTimer() {
      if (this.readyTimer) {
        clearTimeout(this.readyTimer);
        this.readyTimer = null;
      }
    },
    clearTimers() {
      this.clearBootTimer();
      this.clearReadyTimer();
    },
    stallWith(stall) {
      this.clearTimers();
      this.phase = 'stalled';
      this.stall = stall;
    },
    fail(message) {
      this.clearTimers();
      this.phase = 'failed';
      this.failure = message;
    },
    // The session is over, from the app's side or by its clock: say so, and
    // make sure the API has it closed too.
    onEnded(reason, message) {
      if (this.phase === 'ended') return;
      this.clearTimers();
      this.confirmingEnd = false;
      this.phase = 'ended';
      this.endedReason = reason;
      this.endedMessage = message;
      this.$store.dispatch(RELEASE_SUPPORT_SESSION, { reason: reason === 'signed_out' ? 'signed_out' : 'ended' });
    },
    tick() {
      this.now = Date.now();
      if (this.phase !== 'ended' && this.session.expiresAt && this.remainingMs <= 0) {
        this.onEnded('expired', null);
      }
    },
    // Loads the app afresh on the page it is on: it asks for its session
    // again and opens that page once signed in.
    reload() {
      if (this.phase !== 'ended') this.retry();
    },
    retry() {
      this.clearTimers();
      this.phase = 'booting';
      this.stall = null;
      this.failure = '';
      this.frameLoaded = false;
      this.appReady = false;
      this.framePath = this.path || '/';
      this.frameKey += 1;
      this.armBootTimer();
    },
    askEnd() {
      if (this.ending) return;
      this.confirmingEnd = true;
    },
    async confirmEnd() {
      if (this.ending) return;
      this.ending = true;
      const name = this.session.organizationName;
      try {
        this.sendToFrame({ type: SUPPORT_SESSION_MESSAGE.END });
        // Closes the desktop: the session leaves the store, and App.vue
        // takes this down.
        await this.$store.dispatch(END_SUPPORT_SESSION, { reason: 'ended' });
        this.$toast.success(`Ended the session with ${name}.`, TOAST_GLOBAL_CONFIG);
      } finally {
        this.ending = false;
        this.confirmingEnd = false;
      }
    },
    // Once the session is over: back to the admin.
    dismiss() {
      this.$store.dispatch(DISMISS_SUPPORT_SESSION);
    },
  },
  mounted() {
    this.frameOrigin = supportSessionOrigin(this.session);
    window.addEventListener('message', this.onMessage);
    document.body.classList.add(BODY_CLASS);
    this.ticker = setInterval(this.tick, 1000);
    this.tick();
    this.armBootTimer();
  },
  beforeUnmount() {
    // Going without the session having ended (the admin signed out): the app
    // is told, so it forgets the session too.
    if (this.phase !== 'ended') this.sendToFrame({ type: SUPPORT_SESSION_MESSAGE.END });
    window.removeEventListener('message', this.onMessage);
    document.body.classList.remove(BODY_CLASS);
    if (this.ticker) clearInterval(this.ticker);
    this.clearTimers();
  },
};
</script>
<style scoped>
/* ------------------------------- the desk ------------------------------- */
.ssd {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(120% 90% at 50% -10%, #2b3557 0%, #171c30 52%, #0c0f1d 100%);
  color: #E6E9F2;
  font-family: var(--font-sans);
}

.ssd__desk {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  min-height: 0;
  padding: 22px 28px 10px;
}

/* ------------------------------ the monitor ----------------------------- */
.ssd__monitor {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  width: 100%;
  max-width: 1720px;
  min-height: 0;
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 18px;
  background: linear-gradient(180deg, #131727 0%, #0c0f1a 100%);
  box-shadow:
    0 40px 90px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(0, 0, 0, 0.6),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

/* The bar: what and who, where, and the controls. */
.ssd__bar {
  display: flex;
  flex: none;
  align-items: center;
  gap: 14px;
  height: 54px;
  padding: 0 6px 10px 4px;
}

.ssd__brand {
  display: flex;
  flex: 1 1 0;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.ssd__mark {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #ffffff;
}

.ssd__mark img {
  width: auto;
  height: 20px;
}

.ssd__titles {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ssd__title {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.2;
}

.ssd__app {
  white-space: nowrap;
}

.ssd__pill {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 5px;
  height: 20px;
  padding: 0 8px;
  border: 1px solid rgba(255, 176, 92, 0.45);
  border-radius: var(--radius-pill);
  background: rgba(255, 176, 92, 0.14);
  color: #FFC27A;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
}

.ssd__pill .pi {
  font-size: 0.7rem;
}

.ssd__subtitle {
  overflow: hidden;
  color: rgba(230, 233, 242, 0.62);
  font-size: 0.74rem;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ssd__address {
  display: flex;
  flex: 1.4 1 0;
  align-items: center;
  gap: 8px;
  max-width: 640px;
  height: 36px;
  min-width: 0;
  padding: 0 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-pill);
  background: rgba(255, 255, 255, 0.06);
  font-size: 0.8rem;
  white-space: nowrap;
}

.ssd__address .pi {
  flex: none;
  color: #6EE7B7;
  font-size: 0.72rem;
}

.ssd__host {
  flex: none;
  color: #F3F4F8;
  font-weight: 600;
}

.ssd__path {
  overflow: hidden;
  color: rgba(230, 233, 242, 0.6);
  text-overflow: ellipsis;
}

.ssd__controls {
  display: flex;
  flex: 1 1 0;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;
}

.ssd__timer {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 12px;
  border-radius: var(--radius-pill);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(230, 233, 242, 0.85);
  font-size: 0.76rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  cursor: default;
}

.ssd__timer .pi {
  font-size: 0.74rem;
}

.ssd__timer.is-low {
  background: rgba(245, 158, 11, 0.18);
  color: #FCD34D;
}

/* A green dot that breathes while the session is in use. */
.ssd__live {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #34D399;
  box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.55);
  animation: ssd-breathe 2s ease-out infinite;
}

.ssd__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  color: #E6E9F2;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}

.ssd__btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.ssd__btn:focus-visible,
.ssd__end:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.55);
}

.ssd__btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.ssd__btn .pi {
  font-size: 0.85rem;
}

.ssd__end {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #F87171 !important;
  border-radius: 10px;
  background: #EF4444;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.35);
  transition: background-color .15s ease, box-shadow .15s ease;
}

.ssd__end:hover:not(:disabled) {
  background: #DC2626;
  box-shadow: 0 6px 18px rgba(239, 68, 68, 0.45);
}

.ssd__end:disabled {
  cursor: wait;
  opacity: 0.8;
}

.ssd__end .pi {
  font-size: 0.8rem;
}

.ssd__end--close {
  border-color: rgba(255, 255, 255, 0.2) !important;
  background: rgba(255, 255, 255, 0.12);
  box-shadow: none;
}

.ssd__end--close:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.2);
  box-shadow: none;
}

/* ------------------------------- the screen ----------------------------- */
.ssd__screen {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  border-radius: 10px;
  background: #ffffff;
}

.ssd__frame {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  background: #ffffff;
}

/* Over the frame while it can't be used. */
.ssd__veil {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: var(--color-bg-page);
}

.ssd__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(100%, 440px);
  padding: 32px 28px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-bg-canvas);
  box-shadow: var(--shadow-lg);
  text-align: center;
}

.ssd__card-title {
  margin: 18px 0 6px;
  color: var(--color-text-primary);
  font-size: 1.05rem;
  font-weight: 600;
}

.ssd__card-text {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  line-height: 1.55;
}

.ssd__card-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 22px;
}

.ssd__card-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0.55rem 1.1rem;
  font-size: 0.875rem;
}

/* The app's mark inside a turning ring, while it signs in. */
.ssd__spinner {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
}

.ssd__spinner::before {
  content: '';
  position: absolute;
  inset: 0;
  border: 3px solid var(--color-primary-soft-2);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: ssd-spin 1s linear infinite;
}

.ssd__spinner img {
  width: auto;
  height: 26px;
}

.ssd__glyph {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
  font-size: 1.3rem;
}

.ssd__glyph--warn {
  background: #FFF4E5;
  color: #C2680B;
}

/* -------------------------------- the stand ----------------------------- */
.ssd__stand {
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: center;
}

.ssd__neck {
  width: 120px;
  height: 22px;
  background: linear-gradient(180deg, #1d2236 0%, #0f1322 100%);
  clip-path: polygon(14% 0, 86% 0, 100% 100%, 0 100%);
}

.ssd__base {
  width: 300px;
  height: 8px;
  border-radius: 0 0 10px 10px;
  background: linear-gradient(180deg, #232941 0%, #10131f 100%);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

/* ------------------------------ full screen ----------------------------- */
.ssd--max .ssd__desk {
  padding: 0;
}

.ssd--max .ssd__monitor {
  max-width: none;
  padding: 0;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.ssd--max .ssd__bar {
  height: 56px;
  padding: 0 14px;
}

.ssd--max .ssd__screen {
  border-radius: 0;
}

.ssd--max .ssd__stand {
  display: none;
}

@keyframes ssd-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes ssd-breathe {
  0% {
    box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.55);
  }
  70% {
    box-shadow: 0 0 0 7px rgba(52, 211, 153, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(52, 211, 153, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ssd__live,
  .ssd__spinner::before {
    animation: none;
  }
}

/* ------------------------------ smaller screens ------------------------- */
@media (max-width: 1200px) {
  .ssd__address {
    flex: 1 1 0;
  }
}

@media (max-width: 991.98px) {
  .ssd__desk {
    padding: 0;
  }

  .ssd__monitor {
    padding: 0;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  .ssd__bar {
    height: auto;
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px 12px;
  }

  .ssd__brand {
    flex: 1 1 auto;
  }

  .ssd__address {
    display: none;
  }

  .ssd__controls {
    flex: none;
  }

  .ssd__timer {
    display: none;
  }

  .ssd__screen {
    border-radius: 0;
  }

  .ssd__stand {
    display: none;
  }
}

@media (max-width: 575.98px) {
  .ssd__subtitle {
    display: none;
  }

  .ssd__end span:last-child {
    display: none;
  }

  .ssd__end {
    width: 36px;
    padding: 0;
    justify-content: center;
  }
}
</style>
